
import { useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useConnectivity } from "@/services/connectivity-service";
import { queueCoordinator } from "@/services/queue-coordinator";
import { offlineStorage } from "@/services/offline-storage-service";
import { logger } from "@/utils/logger";

export function OfflineRecordingManager() {
  const { user } = useAuth();
  const { isOnline, wasOffline } = useConnectivity();

  // Process queued recordings when we come back online
  useEffect(() => {
    if (user && isOnline && wasOffline) {
      logger.info("Connection restored - processing offline recordings");
      // Wait a bit to ensure connection is stable
      const timer = setTimeout(async () => {
        // Reset any errored recordings so they can be retried
        const resetCount = await offlineStorage.resetErroredRecordings(user.id);
        if (resetCount > 0) {
          logger.info(`Reset ${resetCount} errored recording(s) for retry`);
        }
        await queueCoordinator.processQueue(user.id, 'connectivity_restored');
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [isOnline, wasOffline, user]);

  // Periodically check for failed recordings and retry them in the background
  useEffect(() => {
    if (!user || !isOnline) return;

    // Initial check after 5 seconds - reset errored recordings so they get picked up
    const initialTimer = setTimeout(async () => {
      logger.info("Initial background check for failed recordings");
      const resetCount = await offlineStorage.resetErroredRecordings(user.id);
      if (resetCount > 0) {
        logger.info(`Reset ${resetCount} errored recording(s) for retry on startup`);
      }
      await queueCoordinator.processQueue(user.id, 'periodic_check').catch(err => {
        logger.error("Error during initial periodic check:", err);
      });
    }, 5000);

    // Then check every 2 minutes for any failed recordings that need retry
    const interval = setInterval(async () => {
      logger.info("Periodic background check for failed recordings");
      await queueCoordinator.processQueue(user.id, 'periodic_check').catch(err => {
        logger.error("Error during periodic check:", err);
      });
    }, 120000); // 2 minutes

    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
    };
  }, [user, isOnline]);

  // This is a background component that doesn't render anything
  return null;
}
